import {requestData} from 'services'
import {GiftShopCreatorResponse} from 'types'
import {GIFT_SHOP_CACHE_TTL_MS} from 'consts'
import {reduxSelector, reduxUpdateDispatcher} from 'lib/redux'

let lastFetch = 0
let activeFetchPromise: Promise<GiftshopGetAccountResponse | null> | null = null

export interface GiftshopGetAccountResponse
  extends Omit<GiftShopCreatorResponse, 'id' | 'member_id' | 'username'> {}

function getMapReduxCache(): GiftshopGetAccountResponse | null {
  const {
    kycStatus,
    retakeStatus,
    kycRetakeCountDaily,
    originalKycPhoto,
    latestKycPhoto,
  } = reduxSelector('yuserActivationState')
  const {status} = reduxSelector('similarAccountStatus')
  const {sensitivityLevel} = reduxSelector('roboyuSensitivityLevel')

  if (kycStatus === undefined || retakeStatus === undefined) {
    return null
  }
  return {
    status: kycStatus,
    retake_status: retakeStatus,
    similar_status: status,
    sensitivity_level: sensitivityLevel,
    kyc_retake_count_daily: kycRetakeCountDaily,
    original_kyc_photo: originalKycPhoto!,
    latest_kyc_photo: latestKycPhoto!,
  }
}

async function fetchData(): Promise<GiftshopGetAccountResponse | null> {
  try {
    const response = await requestData('giftshop_get_account')

    if (typeof response !== 'string' && response.status === 200) {
      lastFetch = Date.now()
      const {result} = response.data

      reduxUpdateDispatcher('yuserActivationState', {
        kycStatus: result.status,
        retakeStatus: result.retake_status,
        kycRetakeCountDaily: result.kyc_retake_count_daily,
        originalKycPhoto: result.original_kyc_photo,
        latestKycPhoto: result.latest_kyc_photo,
      })
      reduxUpdateDispatcher('roboyuSensitivityLevel', {
        sensitivityLevel: result.sensitivity_level,
      })
      reduxUpdateDispatcher('similarAccountStatus', {
        status: result.similar_status,
      })
      return result
    }
  } catch (e) {
    console.error(e)
  } finally {
    activeFetchPromise = null
  }

  return getMapReduxCache()
}

function isCacheValid() {
  const {kycStatus, retakeStatus} = reduxSelector('yuserActivationState')
  const isDataMissing = kycStatus === undefined || retakeStatus === undefined
  const isCacheExpired = Date.now() - lastFetch > GIFT_SHOP_CACHE_TTL_MS

  return !isCacheExpired && !isDataMissing
}

export async function handleGiftshopGetAccount(
  forceRequest = false,
): Promise<GiftshopGetAccountResponse | null> {
  if (activeFetchPromise) return activeFetchPromise

  if (forceRequest || !isCacheValid()) {
    activeFetchPromise = fetchData()

    return activeFetchPromise
  }

  return getMapReduxCache()
}

export function handleGiftShopInvalidateAccount() {
  lastFetch = 0
  activeFetchPromise = null
}
